import React, { useEffect, useState } from "react"
import {
    endpointGetChannelsWithReports,
    endpointGetStockStatusReport,
    urlTasks,
    endpointGetAccounts,
    urlInk
} from "../services/Endpoints";
import { Loading } from "./Loading";
import Paginator from "./Paginator";
import Autocomplete from "./Autocomplete";
import { faClose, faSearch, faExternalLinkAlt, faListCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from 'react-bootstrap';
import ComparisonReportModal from "./ComparisonReportModal";
import InactiveTitlesModal from "./InactiveTitlesModal";
import MissingPublicationModal from "./MissingPublicationModal";

export function ReportsList() {

    const [loading, setLoading] = useState(false);
    const [books, setBooks] = useState([]);
    const [columns, setColumns] = useState([]); // Dynamic columns
    const [currentPage, setCurrentPage] = useState(1);
    const [totalBooks, setTotalBooks] = useState(0);

    const [channels, setChannels] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState('');

    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState('');

    const [showComparisonReport, setShowComparisonReport] = useState(false);
    const [showInactiveTitles, setShowInactiveTitles] = useState(false);
    const [showMissingPublication, setShowMissingPublication] = useState(false);

    // Default sort by account asc
    const [sortConfig, setSortConfig] = useState({ key: 'account', direction: 'asc' });

    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');

    // Controla si el usuario ya ejecutó una búsqueda al menos una vez
    const [hasSearched, setHasSearched] = useState(false);

    useEffect(() => {
        getChannels();
        getAccounts();
        return () => {
            setBooks(() => []);
            setChannels(() => []);
            setAccounts(() => []);
        }
    }, []);

    // El reporte solo recarga si ya se buscó antes y cambia la página o el sort
    useEffect(() => {
        if (hasSearched) {
            getReports();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortConfig]);


    const getReports = async (overridePage) => {
        try {
            setLoading(true);
            const pageToUse = overridePage !== undefined ? overridePage : currentPage;
            const sortParam = sortConfig.key ? `&sort=${sortConfig.key}:${sortConfig.direction}` : '';
            const channelParam = selectedChannel ? `&channel=${selectedChannel}` : '';
            const response = await fetch(
                `${urlTasks}${endpointGetStockStatusReport}?page=${pageToUse}&limit=10&filter=${search}&account=${selectedAccount}${channelParam}${sortParam}`,
                { method: "GET", headers: { "Content-Type": "application/json" } }
            );
            const res = await response.json();
            if (res && res.status === 'success') {
                setTotalBooks(res.data.total_count || 0);
                setBooks(res.data.data || []);
                setColumns(res.data.columns || []);
            } else {
                setBooks([]);
                setColumns([]);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error('Error fetching reports:', error);
        }
    }

    const getChannels = async () => {
        try {
            const response = await fetch(`${urlTasks}${endpointGetChannelsWithReports}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setChannels(res.data.channels || []);
            } else {
                setChannels([]);
            }
        } catch (error) {
            console.error('Error fetching channels:', error);
        }
    }

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleChannelChange = (e) => {
        setSelectedChannel(e.target.value);
        // Reiniciar tabla al cambiar tienda (requiere nueva búsqueda)
        setBooks([]);
        setColumns([]);
        setHasSearched(false);
        setCurrentPage(1);
    };

    // Ejecutar búsqueda principal (reporte de stock)
    const handleRunSearch = () => {
        setHasSearched(true);
        setCurrentPage(1);
        getReports(1);
    };

    const getAccounts = async () => {
        try {
            const response = await fetch(`${urlInk}${endpointGetAccounts}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setAccounts(res.data);
            } else {
                setAccounts([]);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    }

    const handleAccountChange = (option) => {
        setSelectedAccount(option.value);
        setCurrentPage(1);
        // Limpiar tabla — el usuario debe volver a buscar
        setBooks([]);
        setColumns([]);
        setHasSearched(false);
    };

    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearch('');
        setCurrentPage(1);
        setBooks([]);
        setColumns([]);
        setHasSearched(false);
    };

    const handleClickSearch = () => {
        setSearch(searchInput);
        setCurrentPage(1);
        setHasSearched(true);
        getReports(1);
    };

    const handleKeyPressSearch = (e) => {
        if (e.key === 'Enter') {
            setSearch(searchInput);
            setCurrentPage(1);
            setHasSearched(true);
            getReports(1);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        // No resetear hasSearched, la paginación ya recargará
    };

    // Filter columns based on selected channel
    const visibleColumns = selectedChannel
        ? columns.filter(col => !col.is_channel || col.channel_alias === selectedChannel)
        : columns;

    return (
        <>
            {/* Filters */}
            <div className="row mb-3 align-items-end">
                <div className="col-md-3">
                    <label>Cuenta</label>
                    <Autocomplete options={accounts} onChange={handleAccountChange} />
                    {selectedAccount !== '' &&
                        <div className="d-flex tag mt-1">
                            <div>{selectedAccount}</div>
                            <div className="close action" onClick={() => handleAccountChange({ value: '' })}>
                                <FontAwesomeIcon icon={faClose} />
                            </div>
                        </div>
                    }
                </div>

                <div className="col-md-3">
                    <label>Tienda</label>
                    <select className="form-select" value={selectedChannel} onChange={handleChannelChange}>
                        <option value="">Todas las tiendas</option>
                        {channels.map((channel) => (
                            <option key={channel.alias} value={channel.alias}>
                                {channel.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col-md-4">
                    <label>Buscar por título, ISBN o INK ID</label>
                    <div className="d-flex search">
                        <input
                            type="text"
                            placeholder="Título, ISBN, INK ID"
                            value={searchInput}
                            onKeyDown={handleKeyPressSearch}
                            onChange={handleSearchInputChange}
                        />
                        {searchInput !== '' && (
                            <button onClick={handleClearSearch} disabled={loading} title="Limpiar búsqueda">
                                <FontAwesomeIcon icon={faClose} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="col-md-2">
                    <button
                        className="btn btn-ink-search w-100 d-flex align-items-center justify-content-center gap-2"
                        onClick={handleRunSearch}
                        disabled={loading}
                        title="Cargar el reporte con los filtros seleccionados"
                    >
                        <FontAwesomeIcon icon={faSearch} />
                        {loading ? 'Cargando…' : 'Buscar'}
                    </button>
                </div>
            </div>

            {/* Actions */}
            <div className="row mb-3">
                <div className="d-flex justify-content-end gap-2 align-items-center">
                    {/* Títulos inactivos — requiere tienda seleccionada */}
                    <button
                        className="btn btn-warning d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => setShowInactiveTitles(true)}
                        disabled={loading || !selectedChannel}
                        title={!selectedChannel ? 'Selecciona una tienda para ver los títulos inactivos' : 'Ver títulos con status inactivo en la tienda'}
                    >
                        <FontAwesomeIcon icon={faListCheck} />
                        Títulos no completados
                    </button>

                    <button className="btn btn-primary d-flex align-items-center gap-2 shadow-sm" onClick={() => setShowComparisonReport(true)} disabled={loading} title="Recargar lista">
                        Reporte de estados
                    </button>
                    <ComparisonReportModal
                        show={showComparisonReport}
                        setShow={setShowComparisonReport}
                        channel={selectedChannel}
                        channels={channels}
                    />

                    <button
                        className="btn btn-success d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => setShowMissingPublication(true)}
                        disabled={loading || !selectedChannel}
                        title={!selectedChannel ? 'Selecciona una tienda para ver los reportes' : 'Ver reportes de títulos que faltan publicar en la tienda'}
                    >
                        <FontAwesomeIcon icon={faListCheck} />
                        Faltan publicar
                    </button>
                    <MissingPublicationModal
                        show={showMissingPublication}
                        setShow={setShowMissingPublication}
                        channel={selectedChannel}
                        channels={channels}
                        account={selectedAccount || ''}
                    />
                    <InactiveTitlesModal
                        isOpen={showInactiveTitles}
                        onClose={() => setShowInactiveTitles(false)}
                        channel={selectedChannel}
                        channelLabel={channels.find(c => c.alias === selectedChannel)?.name || selectedChannel}
                        account={selectedAccount || ''}
                        status="off"
                    />
                </div>
            </div>

            {hasSearched && (
            <Paginator totalItems={totalBooks} itemsPerPage={10} onPageChange={handlePageChange} currentPage={currentPage} />
        )}

            {loading === true && <Loading />}

            {/* Estado inicial — aún no se ha buscado */}
            {!hasSearched && !loading && (
                <div className="text-center py-5" style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)' }}>
                    <div style={{ fontSize: '3rem', marginBottom: 12 }}>🔍</div>
                    <p style={{ fontSize: '1rem', marginBottom: 4 }}>
                        Selecciona los filtros y haz clic en <strong style={{ color: 'var(--ink-text)' }}>Buscar</strong> para cargar el reporte.
                    </p>
                    <p style={{ fontSize: '0.85rem', color: 'rgba(var(--ink-overlay-rgb),0.25)' }}>
                        Puedes filtrar por cuenta, tienda o texto libre.
                    </p>
                </div>
            )}

            {/* Dynamic Table */}
            {hasSearched && (
            <div className="modern-table-container">
                <table className="modern-table table-hover">
                    <thead>
                        <tr>
                            {visibleColumns.map((col, idx) => {
                                const isFixed = !col.is_channel;
                                const style = { whiteSpace: 'nowrap' };
                                let className = "";

                                if (isFixed) {
                                    className = "sticky-col";
                                    if (col.key === 'title') { style.left = 0; style.width = '250px'; style.minWidth = '250px'; }
                                    else if (col.key === 'account') { style.left = '250px'; style.width = '150px'; style.minWidth = '150px'; }
                                    else if (col.key === 'ink_id') { style.left = '400px'; style.width = '80px'; style.minWidth = '80px'; }
                                    else if (col.key === 'identifiers') { style.left = '480px'; style.width = '250px'; style.minWidth = '250px'; }
                                }

                                return (
                                    <th
                                        key={idx}
                                        className={className}
                                        style={{ ...style, cursor: (col.key === 'account' || col.key === 'ink_id') ? 'pointer' : 'default' }}
                                        onClick={() => (col.key === 'account' || col.key === 'ink_id') && handleSort(col.key)}
                                    >
                                        {col.label}
                                        {sortConfig.key === col.key && (
                                            <span style={{ marginLeft: '5px' }}>
                                                {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                            </span>
                                        )}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {books.length === 0 && !loading && (
                            <tr>
                                <td colSpan={visibleColumns.length} className="text-center p-3">
                                    No hay datos disponibles
                                </td>
                            </tr>
                        )}
                        {books.map((book, rowIdx) => (
                            <tr key={rowIdx}>
                                {visibleColumns.map((col, colIdx) => {
                                    if (col.is_channel) {
                                        const channelData = book.channels[col.channel_alias];
                                        if (!channelData) return <td key={colIdx}>-</td>;

                                        // Determinar qué estado mostrar (inkit o report)
                                        const statusData = col.type === 'report' ? channelData.report : channelData.inkit;
                                        if (!statusData) return <td key={colIdx}>-</td>;

                                        let badgeClass = 'status-unselected';
                                        if (statusData.value === 'on') badgeClass = 'status-on';
                                        if (statusData.value === 'off') badgeClass = 'status-off';
                                        if (statusData.value === 'disabled') badgeClass = 'status-disabled';
                                        if (statusData.value === 'processing_up') badgeClass = 'status-processing-up';
                                        if (statusData.value === 'processing_down') badgeClass = 'status-processing-down';
                                        if (statusData.value === 'not_found') badgeClass = 'status-not-found';

                                        // Agregar clase de diferencia si aplica
                                        let cellClass = 'text-center';
                                        if (channelData.has_difference && col.type === 'report') {
                                            cellClass += ' status-difference';
                                        }

                                        return (
                                            <td key={colIdx} className={cellClass}>
                                                <span className={`badge-status ${badgeClass}`}>
                                                    {statusData.text}
                                                </span>
                                                {statusData.link && col.type === 'inkit' && (
                                                    <a href={statusData.link} target="_blank" rel="noopener noreferrer" className="ms-2">
                                                        <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
                                                    </a>
                                                )}
                                            </td>
                                        );
                                    } else {
                                        const isFixed = !col.is_channel;
                                        let className = "";
                                        const style = {};
                                        if (isFixed) {
                                            className = "sticky-col";
                                            if (col.key === 'title') { style.left = 0; style.width = '250px'; style.minWidth = '250px'; }
                                            else if (col.key === 'account') { style.left = '250px'; style.width = '150px'; style.minWidth = '150px'; }
                                            else if (col.key === 'ink_id') { style.left = '400px'; style.width = '80px'; style.minWidth = '80px'; }
                                            else if (col.key === 'identifiers') { style.left = '480px'; style.width = '250px'; style.minWidth = '250px'; }
                                        }
                                        return <td key={colIdx} className={className} style={style}>{book[col.key] || '-'}</td>;
                                    }
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}

        </>
    )
}

export default ReportsList;
